import { useEffect } from 'react';
import { useSocketContext } from '@/contexts/socket-context';
import type {
    ProvisioningEventsPush,
    ProvisioningStatusPush,
} from '@/services/provisioning';

/**
 * Live provisioning updates for one job.
 *
 * Central emits to the room of the admin who clicked Provision, so the
 * handlers filter by `jobId` — the same correlation-id-inside-the-handler
 * idiom as `checkout-form.tsx`, which filters on `data.paymentId`.
 *
 * The socket is a bonus, never the source of truth: `lib/socket.ts` gives up
 * permanently after five reconnect attempts, and a provisioning run lasts tens
 * of minutes. `useProvisioningLog` polls regardless.
 */
export function useProvisioningSocket(
    jobId: string | undefined,
    handlers: {
        onEvents?: (push: ProvisioningEventsPush) => void;
        onStatus?: (push: ProvisioningStatusPush) => void;
    },
) {
    const { socket, isConnected } = useSocketContext();
    const { onEvents, onStatus } = handlers;

    useEffect(() => {
        if (!socket || !jobId) return;

        const handleEvents = (data: ProvisioningEventsPush) => {
            if (data.jobId === jobId) onEvents?.(data);
        };
        const handleStatus = (data: ProvisioningStatusPush) => {
            if (data.jobId === jobId) onStatus?.(data);
        };

        socket.on('provisioning:events', handleEvents);
        socket.on('provisioning:status', handleStatus);

        return () => {
            socket.off('provisioning:events', handleEvents);
            socket.off('provisioning:status', handleStatus);
        };
    }, [socket, jobId, onEvents, onStatus]);

    return { isConnected };
}
